import { Labels } from "../model/Labels.js";
import { DiamondButton } from "./DiamondButton.js";
import { Polygon } from "./Polygon.js";

export interface ConfettiOptions {
    container: HTMLElement;
    colors?: string[];
    labels: Labels;
}

/**
 * @source: Modified https://www.cssscript.com/demo/confetti-falling-animation/
 */
export class Confetti {


    public onClickInstrument = e5.Signal.create();
    public onClickMelody = e5.Signal.create();
    public onClickClose = e5.Signal.create();

    private _element: HTMLElement;
    private _canvas: HTMLCanvasElement;

    private _maxParticleCount = 200; //set max confetti count
    private _particleSpeed = 2; //set the particle animation speed
    private _colors: string[] = [];
    private _streaming = false;
    private _animationTimer: any = null;
    private _particles: any[] = [];
    private _waveAngle = 0;
    private _centralForce: number = 20;
    private _centralRadius: number = 340;
    private _centralRadiusBoxed: number = 0;

    constructor(options: ConfettiOptions) {
        options = e5.object.merge({
            colors: ["DodgerBlue", "OliveDrab", "Gold", "Pink", "SlateBlue", "LightBlue", "Violet", "PaleGreen", "SteelBlue", "SandyBrown", "Chocolate", "Crimson"]
        }, options);

        this._colors = options.colors;

        this._element = document.createElement("div");
        this._element.className = "confetti confetti--hidden";
        this._element.innerHTML = `
        <canvas class="confetti__canvas"></canvas>
        <div class="confetti__box">
            <div class="confetti__title"></div>
            <div class="confetti__subtitle"></div>
            <div class="confetti__buttons"></div>
        </div>
        `;
        this._canvas = this._element.querySelector(".confetti__canvas");

        let onResize = e5.Resize.create(this._element);
        onResize.add(() => {
            this._canvas.width = this._element.offsetWidth;
            this._canvas.height = this._element.offsetHeight;
        });

        new Polygon({
            container: this._element.querySelector(".confetti__box"),
            shape: "13",
            prepend: true
        });

        const newInstrument = new DiamondButton({
            container: this._element.querySelector(".confetti__buttons"),
            label: options.labels.text("melody.confetti.button.new_instrument"),
            className: "confetti__button",
            onClick: () => {
                this.onClickInstrument.dispatch();
            }
        });

        const newMelody = new DiamondButton({
            container: this._element.querySelector(".confetti__buttons"),
            label: options.labels.text("melody.confetti.button.new_melody"),
            className: "confetti__button",
            onClick: () => {
                this.onClickMelody.dispatch();
            }
        });

        options.container.appendChild(this._element);
    }

    public get title(): string {
        return this._element.querySelector(".confetti__title").textContent;
    }

    public set title(value: string) {
        this._element.querySelector(".confetti__title").textContent = value;
    }

    public get subtitle(): string {
        return this._element.querySelector(".confetti__subtitle").textContent;
    }

    public set subtitle(value: string) {
        this._element.querySelector(".confetti__subtitle").textContent = value;
    }

    private reset(particle: any, width: number, height: number) {
        particle.color = this._colors[(Math.random() * this._colors.length) | 0];
        particle.x = Math.random() * width;
        particle.y = Math.random() * height - height;
        particle.diameter = Math.random() * 10 + 5;
        particle.tilt = Math.random() * 10 - 10;
        particle.tiltAngleIncrement = Math.random() * 0.07 + 0.05;
        particle.tiltAngle = 0;
        return particle;
    }

    private get width(): number {
        return this._canvas.offsetWidth;
    }

    private get height(): number {
        return this._canvas.offsetHeight;
    }

    public get visible(): boolean {
        return !this._element.classList.contains("confetti--hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this._element.classList.toggle("confetti--hidden", !value);
        if (value) {
            this.start();
        } else {
            this.stop();
        }
    }

    public get boxed(): boolean {
        return this._element.classList.contains("confetti--boxed");
    }

    public set boxed(value: boolean) {
        if (this.boxed === value) {
            return;
        }
        this._element.classList.toggle("confetti--boxed", value);
    }

    private start() {
        var width = this.width;
        var height = this.height;
        let requestAnimFrame = (function () {
            return window.requestAnimationFrame ||
                (window as any).webkitRequestAnimationFrame ||
                (window as any).mozRequestAnimationFrame ||
                (window as any).oRequestAnimationFrame ||
                (window as any).msRequestAnimationFrame ||
                function (callback: any) {
                    return window.setTimeout(callback, 16.6666667);
                };
        })();

        var canvas = this._canvas;
        var context = canvas.getContext("2d");

        while (this._particles.length < this._maxParticleCount) {
            this._particles.push(this.reset({}, width, height));
        }

        this._streaming = true;
        if (this._animationTimer === null) {
            const runAnimation = () => {
                context.clearRect(0, 0, this.width, this.height);
                if (this._particles.length === 0)
                    this._animationTimer = null;
                else {
                    this.updateParticles();
                    this.drawParticles(context);
                    this._animationTimer = requestAnimFrame(runAnimation);
                }
            };
            runAnimation();
        }
    }

    private stop() {
        this._streaming = false;
    }

    private toggle() {
        if (this._streaming)
            this.stop();
        else
            this.start();
    }

    private drawParticles(context: CanvasRenderingContext2D) {
        var particle;
        var x;
        for (var i = 0; i < this._particles.length; i++) {
            particle = this._particles[i];
            context.beginPath();
            context.lineWidth = particle.diameter;
            context.strokeStyle = particle.color;
            x = particle.x + particle.tilt;
            context.moveTo(x + particle.diameter / 2, particle.y);
            context.lineTo(x, particle.y + particle.tilt + particle.diameter / 2);
            context.stroke();
        }
    }

    private updateParticles() {
        var width = this._canvas.offsetWidth;
        var height = this._canvas.offsetHeight;
        var particle;
        this._waveAngle += 0.01;

        let cx = width * 0.5;
        let cy = height * 0.4;
        let centralRadius = this.boxed ? this._centralRadiusBoxed : this._centralRadius;

        for (var i = 0; i < this._particles.length; i++) {
            particle = this._particles[i];
            if (!this._streaming && particle.y < -15)
                particle.y = height + 100;
            else {
                particle.tiltAngle += particle.tiltAngleIncrement;
                particle.x += Math.sin(this._waveAngle);
                particle.y += (Math.cos(this._waveAngle) + particle.diameter + this._particleSpeed) * 0.5;
                particle.tilt = Math.sin(particle.tiltAngle) * 15;

                if (centralRadius > 0
                    && this._centralForce > 0) {

                    let dx = cx - particle.x;
                    let dy = cy - particle.y;
                    let dist = Math.sqrt(dx * dx + dy * dy);

                    if (dist < centralRadius) {
                        let force = this._centralForce * (1 - (dist / 300));
                        if (dx < 0) {
                            particle.x += Math.abs(force);
                        } else {
                            particle.x -= Math.abs(force);
                        }
                    }
                }

            }
            if (particle.x > width + 20 || particle.x < -20 || particle.y > height) {
                if (this._streaming && this._particles.length <= this._maxParticleCount)
                    this.reset(particle, width, height);
                else {
                    this._particles.splice(i, 1);
                    i--;
                }
            }
        }
    }
}